import { motion } from 'framer-motion';

interface TypingDotsProps {
  /** Color of the dots — defaults to the primary brand color. */
  color?: string;
  /** Diameter of each dot in pixels. */
  size?: number;
  /** Optional label rendered next to the dots (e.g. "L'IA réfléchit…"). */
  label?: string;
  className?: string;
}

/**
 * Reusable "typing" loader inspired by chat UIs.
 *
 * Used to give visual feedback during AI streaming steps (suggestions de sujets/sources,
 * réflexion d'un agent, etc.). Trois points qui pulsent à 200ms d'intervalle pour rester
 * cohérent avec l'identité Raiso : easing smooth + couleur primaire par défaut.
 */
export function TypingDots({
  color = 'var(--color-primary)',
  size = 6,
  label,
  className = '',
}: TypingDotsProps) {
  const dotVariants = {
    initial: { y: 0, opacity: 0.4 },
    animate: { y: [0, -4, 0], opacity: [0.4, 1, 0.4] },
  };

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      role="status"
      aria-live="polite"
      aria-label={label || 'Loading'}
    >
      <div className="flex items-center" style={{ gap: size / 2 }}>
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            variants={dotVariants}
            initial="initial"
            animate="animate"
            transition={{
              duration: 1.0,
              ease: 'easeInOut',
              repeat: Infinity,
              delay: i * 0.18,
            }}
            style={{
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: color,
              display: 'inline-block',
            }}
          />
        ))}
      </div>
      {label && (
        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          {label}
        </span>
      )}
    </div>
  );
}
